import { AlertError } from "@utils/alert";
import React, {
  ComponentProps,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./ImageInput.scss";

interface ImageInputProps extends ComponentProps<"input"> {
  error?: string;
  touched?: boolean;
  title?: string;
  list?: string;
  onChangeCrop: (x: string) => void;
  initialValue: string | null;
}

const validateImageSize = (size: number) => {
  //Validate Image Size only max 2MB
  if (size > 2100000) {
    return false;
  }

  return true;
};

const ImageInput = ({
  error,
  touched,
  title,
  onChangeCrop,
  initialValue,
  ...props
}: ImageInputProps) => {
  const [upImg, setUpImg] = useState<any>();
  const imgRef = useRef<HTMLImageElement | any>(null);
  const previewCanvasRef = useRef<HTMLImageElement | any>(null);
  const [crop, setCrop] = useState<HTMLImageElement | any>({
    unit: "%",
    width: 30,
    aspect: 1 / 1,
  });
  const [completedCrop, setCompletedCrop] = useState<HTMLImageElement | any>(
    null
  );
  const [isImageCancel, setIsImageCancel] = useState<boolean>(false);
  const [isImageComplete, setIsImageComplete] = useState<boolean>(false);
  const [resultImg, setResultImg] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const onSelectFile = (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsImageCancel(false);
      setIsImageComplete(false);

      const validateSize = validateImageSize(e.target.files[0].size);
      if (!validateSize) {
        setUpImg(null);
        setIsImageCancel(true);
        setIsImageComplete(false);
        onChangeCrop("");
        e.target.value = "";

        return AlertError(
          "Validate Image",
          "Max 2MB file is allowed for image."
        );
      }

      const reader = new FileReader();
      if (reader) {
        reader.addEventListener("load", () => setUpImg(reader.result));
        reader.readAsDataURL(e.target.files[0]);
      }
    }
  };

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    setResultImg(canvas.toDataURL("image/jpeg"));
  }, [completedCrop]);

  const onCancel = () => {
    setUpImg(null);
    setIsImageCancel(true);
    setIsImageComplete(false);
    onChangeCrop("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const onSetCrop = () => {
    setIsImageComplete(true);
    onChangeCrop(resultImg);
  };

  return (
    <div className="formElements__group">
      {title ? <label className="formElements__label">{title}</label> : null}
      <input
        type="file"
        accept="image/jpeg, image/jpg, image/png"
        className={`w-100 form-control formElements__input formElements--inputText
            ${touched && error ? "formElements__errorBorder" : ""}
            `}
        onChange={onSelectFile}
        ref={inputRef}
        {...props}
      />

      {upImg && isImageComplete && resultImg !== "" && (
        <div>
          <img
            style={{
              width: Math.round(completedCrop?.width ?? 0),
              height: Math.round(completedCrop?.height ?? 0),
              marginTop: 20,
            }}
            src={resultImg}
          />
        </div>
      )}

      {!upImg && initialValue && (
        <div>
          <img
            style={{
              width: 175,
              height: 175,
              marginTop: 20,
            }}
            src={initialValue}
          />
        </div>
      )}

      {upImg && !isImageCancel && !isImageComplete && (
        <>
          <div className="reactCrop__container">
            <div className="reactCrop__topNavContainer">
              <button
                type="button"
                className="mt-3 formElements__buttonCancel reactCrop__button"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mt-3 formElements__button reactCrop__button"
                onClick={onSetCrop}
              >
                Set Crop
              </button>
            </div>

            <div>
              <div>
                <canvas
                  ref={previewCanvasRef}
                  // Rounding is important so the canvas width and height matches/is a multiple for sharpness.
                  style={{
                    width: Math.round(completedCrop?.width ?? 0),
                    height: Math.round(completedCrop?.height ?? 0),
                  }}
                />
              </div>

              <ReactCrop
                src={upImg}
                onImageLoaded={onLoad}
                crop={crop}
                onChange={(c: any) => setCrop(c)}
                onComplete={(c: Crop) => {
                  setCompletedCrop(c);
                }}
              />
            </div>
          </div>
        </>
      )}

      {touched && error ? (
        <div className="formElements__errorText">{error}</div>
      ) : null}
      <div className="invalid-feedback" />
    </div>
  );
};

export default memo(ImageInput);
